import {IMonteCarloResults} from "../../../../components/calculator/Calculator";
import React, {useEffect, useState} from "react";
import {numberToMoneyFormat, numberToPercentFormat} from "../../../../stores/StoreUtilities";
import {globalColors} from "../../../../styles/globalStyles";
import {Box, CircularProgress, IconButton, Link, Paper, TableContainer, Typography} from "@mui/material";
import Chart from "react-apexcharts";
import RateModels from "../../../../components/calculator/RateModels";
import RefreshIcon from '@mui/icons-material/Refresh';

const comparisonOptions = {
  chart: {
    id: "future-view-comparison",
    type: 'line',
    stacked: false,
    height: 350,
    width: "100%",
    events: {
      mounted: (chart: any) => {
        chart.windowResizeHandler();
      }
    },
    toolbar: {
      show: false,
      tools: {
        download: false
      }
    }
  },
  title: {
    text: "",
    align: "center",
    margin: 0,
    floating: true,
    style: {
      fontSize: "16px",
      fontFamily: "Poppins,sans-serif",
      color: "rgb(31, 92, 167)"
    }
  },
  dataLabels: {
    enabled: false
  },
  legend: {
    position: 'top',
    horizontalAlign: 'left'
  },
  xaxis: {
    categories: [] as number[],
    axisBorder: {
      show: true
    }
  },
  yaxis: {
    labels: {
      formatter: (value: number) => {
        // return numberToMoneyFormat(value, 0)
        if (value < 1000000) {
          return `$${value / 1000}K`
        } else {
          return `$${value / 1000000}M`
        }
      }
    },
    min: 0
  },
  tooltip: {
    y: {
      formatter: function (value: number) {
        return numberToMoneyFormat(value, 0)
      }
    }
  },
  // colors: ["rgb(0, 128, 0)", "rgb(51, 122, 183)", "#EB781B", "darkmagenta"],
  colors: globalColors.categorical,
  fill: {
    type: 'none'
  },
  stroke: {
    width: [2, 2, 2, 2, 2, 5],
    dashArray: [5, 5, 5, 5, 5, 0]
  },
  responsive: [{
    breakpoint: undefined,
    options: {}
  }]
}

const MonteCarloChart = ({
  year,
  monteCarloResults,
  height,
  width,
  title,
  hideScore,
  isProcessing,
  onSettings,
  onRefresh
}: {
  year: number
  monteCarloResults: IMonteCarloResults
  height?: string
  width?: string
  title?: string
  hideScore?: boolean
  isProcessing?: boolean
  onSettings?: () => void
  onRefresh?: () => void
}) => {
  const [options, setOptions] = useState<any>()
  const [series, setSeries] = useState<any>()
  const [currentYear, setCurrentYear] = useState<number>(0)

  const styles = {
    progress: {
      display: "flex",
      flexDirection: "column",
      flexGrow: 1,
      width: "100%",
      justifyContent: "center",
      alignItems: "center",
      marginTop: "230px",
      marginLeft: "auto",
      marginRight: "auto",
    },
    inlineLink: {
      paddingBottom: "2px"
    },
  }

  const rateModels = new RateModels()

  useEffect(() => {
    if (monteCarloResults) {
      calculateData()
    }
    setCurrentYear(year)
  }, [monteCarloResults])


  const calculateData = () => {
    const series: any[] = []

    let maxYear = 0
    let categories: number[] = []
    monteCarloResults.labels.forEach((label: string, index: number) => {
      let netData = []
      let years = monteCarloResults.years

      for (let i = 0; i < years.length; i++) {
        const year = years[i]
        if (year.year > maxYear) {
          categories.push(year.year)
          maxYear = year.year
        }
        netData.push(year.balances[index])
      }
      series.push({
        name: label,
        data: netData
      })
    })

    setSeries(series)
    const options = comparisonOptions // cloneDeep(comparisonOptions)
    options.title.text = title ?? ""
    options.xaxis.categories = categories

    if (height) {
      options.chart.toolbar.show = false
    }
    setOptions(options)
  }

  if (options && series) {
    const summary = <Typography variant="h6" sx={{lineHeight: 1.3}} gutterBottom>
      You have a {numberToPercentFormat(monteCarloResults.confidenceScore, 0)} chance of fully funding your retirement using <br/>
      <Link component="button" variant="h6" sx={styles.inlineLink} onClick={() => onSettings && onSettings()}>{rateModels.getRateModelLabel(monteCarloResults.rateModel)}</Link> portfolio assumptions.
    </Typography>

    const details = <Typography variant="body2">
      In this Monte Carlo projection, we ran <Link component="button" variant="body2" sx={styles.inlineLink} onClick={() => onSettings && onSettings()}>{monteCarloResults.simulations}</Link> simulations based on your projected budget, taxes, historic market volatility and annual rebalancing assumptions.  Your chance of success is determined by the percentage of these simulations in which you successfully reach expected longevity age without depleting your funds.
    </Typography>

    return (
      <TableContainer component={Paper} sx={{ paddingTop:1, justifyContent:"center", position: "relative" }}>
        {!hideScore &&
          <Box sx={{
            position: "absolute",
            top: 40,
            left: 70,
            zIndex: 10,
            maxWidth: "50%",
            padding: "8px 12px 8px 12px",
            backgroundColor: "white",
            borderRadius: "4px",
            boxShadow: "0px 2px 4px rgba(0,0,0,0.3)",
            border: "1px solid #eaeaea"
          }}>
            <Box sx={{display:"flex", justifyContent:"space-between"}}>
              <Typography variant="h4" color="primary" sx={{mb: 0.5}}>Retirement Confidence Score: {numberToPercentFormat(monteCarloResults.confidenceScore, 0)}</Typography>
              {isProcessing &&
                <CircularProgress color="info" size={24} disableShrink={true}/>
              }
              {!isProcessing &&
                <IconButton onClick={() => {onRefresh && onRefresh()}} sx={{padding:0, margin:0}}>
                  <RefreshIcon />
                </IconButton>
              }
            </Box>
            {summary}
            {details}
          </Box>
        }
        <Chart
          options={options}
          series={series}
          type={series.length === 1 ? "area" : "line"}
          height={height ?? "500"}
          width={width ?? "100%"}
        />
      </TableContainer>
    )
  } else {
    return (
      <TableContainer component={Paper} sx={{ paddingTop:1, justifyContent:"center", height: 500, width:"100%" }}>
        <Box sx={styles.progress}>
          <CircularProgress/>
        </Box>
      </TableContainer>
    )
  }
}

export default MonteCarloChart